import { Post, PostStatus } from '../types/api';

export type StatusChipColor = 'success' | 'warning' | 'info' | 'error' | 'default';

export const POST_STATUS_LABELS: Record<PostStatus, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  PUBLISHED: 'Published',
  PARTIAL: 'Partial',
  FAILED: 'Failed',
};

export function getPostStatusColor(status: PostStatus): StatusChipColor {
  switch (status) {
    case 'PUBLISHED':
      return 'success';
    case 'PARTIAL':
      return 'warning';
    case 'SCHEDULED':
      return 'warning';
    case 'DRAFT':
      return 'info';
    case 'FAILED':
      return 'error';
    default:
      return 'default';
  }
}

export function getPostStatusLabel(status: PostStatus): string {
  return POST_STATUS_LABELS[status] ?? status;
}

/** Short error or progress line for FAILED / PARTIAL / SCHEDULED rows. */
export function getPostStatusSnippet(post: Post): string | null {
  if (post.status === 'FAILED') {
    const errors = post.publish_errors ? Object.values(post.publish_errors) : [];
    if (errors.length > 0) {
      const first = errors.find((e) => e && e.trim());
      if (first) {
        return first.length > 120 ? `${first.slice(0, 117)}...` : first;
      }
    }
    return 'Publishing failed on all selected platforms.';
  }

  if (post.status === 'PARTIAL') {
    const published = post.published_platforms ?? [];
    const targets = post.platforms ?? [];
    const failed = targets.filter((p) => !published.includes(p));
    const errParts = post.publish_errors
      ? Object.entries(post.publish_errors)
          .filter(([platform]) => failed.includes(platform))
          .map(([, msg]) => msg)
          .filter(Boolean)
      : [];
    if (errParts.length > 0) {
      const snippet = errParts[0] as string;
      return snippet.length > 120 ? `${snippet.slice(0, 117)}...` : snippet;
    }
    if (published.length && failed.length) {
      return `Published: ${published.join(', ')} · Failed: ${failed.join(', ')}`;
    }
    return 'Some platforms published; others failed.';
  }

  if (post.status === 'SCHEDULED') {
    if (post.time_remaining_formatted) {
      return `Publishes in ${post.time_remaining_formatted}`;
    }
    if (post.scheduled_time) {
      return 'Scheduled for publish';
    }
  }

  return null;
}

export function countPostsByStatus(posts: Post[]): Record<PostStatus, number> {
  const counts: Record<PostStatus, number> = {
    DRAFT: 0,
    SCHEDULED: 0,
    PUBLISHED: 0,
    PARTIAL: 0,
    FAILED: 0,
  };
  for (const post of posts) {
    if (counts[post.status] !== undefined) {
      counts[post.status] += 1;
    }
  }
  return counts;
}
